import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import {
    AlignCenter,
    AlignLeft,
    AlignRight,
    Bold,
    Heading1,
    Heading2,
    Heading3,
    Image,
    Italic,
    Link,
    List,
    ListOrdered,
    Underline} from "lucide-react";
import { useState } from "react";

interface ContentEditorProps {
    value: string;
    onChange: (value: string) => void;
}

export const ContentEditor = ({ value, onChange }: ContentEditorProps) => {
    const [selectedText, setSelectedText] = useState("");

    const handleSelect = () => {
        const textarea = document.querySelector("textarea");
        if (textarea) {
            const start = textarea.selectionStart;
            const end = textarea.selectionEnd;
            setSelectedText(textarea.value.substring(start, end));
        }
    };

    const insertFormat = (format: string) => {
        const textarea = document.querySelector("textarea");
        if (textarea) {
            const start = textarea.selectionStart;
            const end = textarea.selectionEnd;
            const text = textarea.value;
            let newText = "";

            switch (format) {
                case "bold":
                    newText = text.substring(0, start) + "**" + selectedText + "**" + text.substring(end);
                    break;
                case "italic":
                    newText = text.substring(0, start) + "_" + selectedText + "_" + text.substring(end);
                    break;
                case "underline":
                    newText = text.substring(0, start) + "__" + selectedText + "__" + text.substring(end);
                    break;
                case "h1":
                    newText = text.substring(0, start) + "# " + selectedText + text.substring(end);
                    break;
                case "h2":
                    newText = text.substring(0, start) + "## " + selectedText + text.substring(end);
                    break;
                case "h3":
                    newText = text.substring(0, start) + "### " + selectedText + text.substring(end);
                    break;
                case "ul":
                    newText = text.substring(0, start) + "- " + selectedText + text.substring(end);
                    break;
                case "ol":
                    newText = text.substring(0, start) + "1. " + selectedText + text.substring(end);
                    break;
                case "link":
                    newText = text.substring(0, start) + "[" + selectedText + "](url)" + text.substring(end);
                    break;
                case "image":
                    newText = text.substring(0, start) + "![alt text](" + selectedText + ")" + text.substring(end);
                    break;
                default:
                    newText = text;
            }

            onChange(newText);
            textarea.focus();
        }
    };

    return (
        <div className="space-y-4">
            <div className="flex flex-wrap gap-2 p-2 bg-muted rounded-lg">
                <TooltipProvider>
                    <div className="flex items-center gap-1 border-r pr-2">
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => insertFormat("h1")}
                                >
                                    <Heading1 className="h-4 w-4" />
                                </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                                <p>Заголовок 1</p>
                            </TooltipContent>
                        </Tooltip>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => insertFormat("h2")}
                                >
                                    <Heading2 className="h-4 w-4" />
                                </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                                <p>Заголовок 2</p>
                            </TooltipContent>
                        </Tooltip>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => insertFormat("h3")}
                                >
                                    <Heading3 className="h-4 w-4" />
                                </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                                <p>Заголовок 3</p>
                            </TooltipContent>
                        </Tooltip>
                    </div>

                    <div className="flex items-center gap-1 border-r pr-2">
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => insertFormat("bold")}
                                >
                                    <Bold className="h-4 w-4" />
                                </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                                <p>Жирний</p>
                            </TooltipContent>
                        </Tooltip>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => insertFormat("italic")}
                                >
                                    <Italic className="h-4 w-4" />
                                </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                                <p>Курсив</p>
                            </TooltipContent>
                        </Tooltip>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => insertFormat("underline")}
                                >
                                    <Underline className="h-4 w-4" />
                                </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                                <p>Підкреслений</p>
                            </TooltipContent>
                        </Tooltip>
                    </div>

                    <div className="flex items-center gap-1 border-r pr-2">
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => insertFormat("ul")}
                                >
                                    <List className="h-4 w-4" />
                                </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                                <p>Маркований список</p>
                            </TooltipContent>
                        </Tooltip>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => insertFormat("ol")}
                                >
                                    <ListOrdered className="h-4 w-4" />
                                </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                                <p>Нумерований список</p>
                            </TooltipContent>
                        </Tooltip>
                    </div>

                    <div className="flex items-center gap-1 border-r pr-2">
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => insertFormat("link")}
                                >
                                    <Link className="h-4 w-4" />
                                </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                                <p>Посилання</p>
                            </TooltipContent>
                        </Tooltip>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => insertFormat("image")}
                                >
                                    <Image className="h-4 w-4" />
                                </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                                <p>Зображення</p>
                            </TooltipContent>
                        </Tooltip>
                    </div>

                    <div className="flex items-center gap-1">
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <Button variant="ghost" size="icon">
                                    <AlignLeft className="h-4 w-4" />
                                </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                                <p>По лівому краю</p>
                            </TooltipContent>
                        </Tooltip>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <Button variant="ghost" size="icon">
                                    <AlignCenter className="h-4 w-4" />
                                </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                                <p>По центру</p>
                            </TooltipContent>
                        </Tooltip>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <Button variant="ghost" size="icon">
                                    <AlignRight className="h-4 w-4" />
                                </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                                <p>По правому краю</p>
                            </TooltipContent>
                        </Tooltip>
                    </div>
                </TooltipProvider>
            </div>

            <Textarea
                value={value}
                onChange={(e) => onChange(e.target.value)}
                onSelect={handleSelect}
                className="min-h-[400px] font-mono"
                placeholder="Введіть або вставте Markdown текст..."
            />

            <div className="p-4 border rounded-lg bg-muted">
                <p className="text-sm text-muted-foreground">
                    Використовуйте Markdown для форматування тексту. Підтримуються заголовки,
                    жирний текст, курсив, списки, посилання та зображення.
                </p>
            </div>
        </div>
    );
}; 